import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';

import type {
  CrossChainFailureReason,
  CrossChainRefundStatus,
  MatchaTradeHistoryEntry,
} from './types';

/** Human-readable labels for cross-chain refund statuses. */
export const REFUND_STATUS_LABELS: Record<CrossChainRefundStatus, string> = {
  refund_succeeded: 'Refunded',
  refund_pending: 'Refund Pending',
  manual_action_required: 'Action Required',
  no_actions_required: 'No Action Needed',
  failed: 'Failed',
  unknown: 'Unknown',
};

/** Human-readable labels for cross-chain failure reasons. */
export const FAILURE_REASON_LABELS: Record<CrossChainFailureReason, string> = {
  expired: 'Bridge expired',
  cancelled: 'Bridge cancelled',
  out_of_gas: 'Out of gas',
  internal: 'Internal bridge error',
  unknown: 'Unknown',
};

/**
 * Headline status labels for cross-chain refunds.
 *
 * When a cross-chain bridge fails, a successful or in-progress refund is the
 * outcome the user actually cares about, so we surface it in place of
 * "Failure"/"Pending" — both of which read as "funds lost" to a user who was
 * refunded. Only the refund states that represent a user-facing
 * terminal/pending outcome are included; `no_actions_required` (healthy trade),
 * `failed`, and `unknown` are intentionally omitted so the underlying
 * settlement-derived status (e.g. "Bridge failed") shows through.
 */
const REFUND_HEADLINE_LABELS: Partial<Record<CrossChainRefundStatus, string>> = {
  refund_succeeded: REFUND_STATUS_LABELS.refund_succeeded,
  refund_pending: REFUND_STATUS_LABELS.refund_pending,
  manual_action_required: REFUND_STATUS_LABELS.manual_action_required,
};

/**
 * Returns the headline label to display for a refunded/refunding cross-chain
 * trade, or `undefined` when the refund status should not override the
 * settlement-derived status.
 */
export const getRefundHeadlineLabel = (
  refundStatus: CrossChainRefundStatus | undefined,
): string | undefined => (refundStatus ? REFUND_HEADLINE_LABELS[refundStatus] : undefined);

/**
 * Single source of truth for a trade's headline status label, shared by the
 * history table, the trade-details modal, and the tx-detail page.
 *
 * A refund (when present) takes precedence over the settlement-derived status —
 * see {@link getRefundHeadlineLabel}. A reported bridge failure comes next:
 * `failureReason` (any value, including `unknown`) is only ever populated when
 * a bridge failure occurred, so its presence headlines the trade "Bridge
 * failed" even while the indexed status still reads pending. Callers should pass
 * `refundStatus`/`failureReason` from the richest source they have (e.g. live
 * 0x status ∪ cached failure context ∪ the indexed row) so a refund surfaces
 * even before the indexed status flips to `bridge_failed`.
 */
export const getTradeStatus = ({
  status,
  liquiditySource,
  refundStatus,
  failureReason,
}: {
  status: string | undefined;
  liquiditySource: string | undefined;
  refundStatus?: CrossChainRefundStatus;
  failureReason?: CrossChainFailureReason;
}): string => {
  const normalizedStatus = (status ?? '').toLowerCase();

  if (liquiditySource === MATCHA_LIMIT_ORDER) {
    return normalizedStatus === 'success' ? 'Filled' : (status ?? '');
  }

  const refundLabel = getRefundHeadlineLabel(refundStatus);
  if (refundLabel) return refundLabel;

  if (failureReason) return 'Bridge failed';

  switch (normalizedStatus) {
    case 'bridge_filled':
      return 'Success';
    case 'bridge_failed':
    case 'origin_tx_reverted':
      return 'Bridge failed';
    case 'bridge_pending':
    case 'origin_tx_pending':
    case 'origin_tx_succeeded':
    case 'origin_tx_confirmed':
    case 'pending':
      return 'Pending';
    case 'error':
      return 'Failed';
    case 'cancelled':
    case 'cancelledsingle':
    case 'cancelledmulti':
      return 'Cancelled';
    default:
      return status ?? '';
  }
};

export const enum TRADE_TYPE {
  MARKET_TRANSFER = 'swap & transfer',
  MARKET = 'swap',
  LIMIT = 'limit',
  CROSS_CHAIN = 'bridge',
}

/**
 * Single source of truth for a trade's display type, shared by the history
 * table, the trade-details modal, and the tx-detail page. Cross-chain is
 * detected from whichever signal the caller's data source carries
 * (`isCrossChainTx` on indexed trades, `destinationChainId` on modal/table
 * rows).
 */
export const getTradeType = ({
  isCrossChainTx,
  destinationChainId,
  liquiditySource,
  recipient,
}: {
  isCrossChainTx?: boolean;
  destinationChainId?: number | null;
  liquiditySource?: string;
  recipient?: string;
}): TRADE_TYPE => {
  if (isCrossChainTx || destinationChainId != null) return TRADE_TYPE.CROSS_CHAIN;
  if (liquiditySource === MATCHA_LIMIT_ORDER) return TRADE_TYPE.LIMIT;
  if (recipient) return TRADE_TYPE.MARKET_TRANSFER;
  return TRADE_TYPE.MARKET;
};

/** Stable per-row identity; fill rows share an order ID, so they need the fill's transaction and index. */
export const getTradeIdentity = (trade: MatchaTradeHistoryEntry): string =>
  trade.limitFill && trade.orderId
    ? `${trade.orderId}:${trade.transactionHash ?? ''}:${trade.limitFill.fillIndex}`
    : (trade.orderId ??
      `${trade.chainId}:${trade.transactionHash ?? ''}:${trade.tradeId}:${trade.timestamp}`);

/** Indexed states that mean the trade settled; also filterable server-side. */
export const SUCCESS_TRADE_STATES = ['success', 'bridge_filled'];

/** Refund statuses that headline a row, so it is not a clean fill. */
export const REFUNDED_TRADE_STATUSES = Object.keys(
  REFUND_HEADLINE_LABELS,
) as CrossChainRefundStatus[];

const SUCCESS_STATES = new Set(SUCCESS_TRADE_STATES);

/**
 * Settled fills only - pending, failed, and refunded rows stay on /history.
 *
 * A limit order's state is a lifecycle label ("Filled", "Cancelled", "Expired")
 * that says nothing about whether anything executed, so it settles on the filled
 * pair instead. Every limit row from /api/trade-history carries that pair.
 */
export const isSuccessfulHistoryEntry = (
  entry: Pick<
    MatchaTradeHistoryEntry,
    'state' | 'refundStatus' | 'liquiditySource' | 'filledMakerAmount' | 'filledTakerAmount'
  >,
): boolean => {
  if (getRefundHeadlineLabel(entry.refundStatus)) return false;
  if (entry.liquiditySource === MATCHA_LIMIT_ORDER) {
    return (entry.filledMakerAmount ?? 0) > 0 && (entry.filledTakerAmount ?? 0) > 0;
  }
  return SUCCESS_STATES.has(entry.state.toLowerCase());
};
